import { create } from "zustand";
import { persist } from "zustand/middleware";
import { debounce } from "../utils/debounce";
import { useLocalLibraryStore } from "./localLibraryStore";

export interface SearchHistoryItem {
  query: string;
  timestamp: number;
  provider: "local" | "spotify";
  resultCount: number;
}

interface SearchState {
  history: SearchHistoryItem[];
  addToHistory: (item: Omit<SearchHistoryItem, "timestamp">) => void;
  clearHistory: () => void;
  getRecentSearches: (limit?: number) => SearchHistoryItem[];
  getSuggestions: (query: string) => string[];
  searchLocalLibrary: (query: string) => any[]; // Return type depends on your local library structure
}

export const useSearchStore = create<SearchState>()(
  persist(
    (set, get) => ({
      history: [],

      addToHistory: debounce((item) => {
        set((state) => {
          const newHistory = [
            {
              ...item,
              timestamp: Date.now(),
            },
            ...state.history.filter((h) => h.query !== item.query).slice(0, 49),
          ];
          return { history: newHistory };
        });
      }, 500),

      clearHistory: () => set({ history: [] }),

      getRecentSearches: (limit = 5) => {
        return get()
          .history.sort((a, b) => b.timestamp - a.timestamp)
          .slice(0, limit);
      },

      getSuggestions: (query) => {
        if (!query) return [];

        const history = get().history;
        const historySuggestions = [
          ...new Set(
            history.filter((item) => item.query.toLowerCase().includes(query.toLowerCase())).map((item) => item.query)
          ),
        ];

        // Get suggestions from local library
        const localLibrary = useLocalLibraryStore.getState();
        const tracks = localLibrary.getAllAlbums().flatMap((album) => album.tracks);
        const localSuggestions = tracks
          .filter(
            (track) =>
              track.title.toLowerCase().includes(query.toLowerCase()) ||
              track.artist.toLowerCase().includes(query.toLowerCase())
          )
          .map((track) => `${track.title} - ${track.artist}`);

        // Combine and deduplicate suggestions
        return [...new Set([...historySuggestions, ...localSuggestions])].slice(0, 5);
      },

      searchLocalLibrary: (query) => {
        const localLibrary = useLocalLibraryStore.getState();
        const albums = localLibrary.getAllAlbums();
        const tracks = albums.flatMap((album) => album.tracks);

        return tracks.filter(
          (track) =>
            track.title.toLowerCase().includes(query.toLowerCase()) ||
            track.artist.toLowerCase().includes(query.toLowerCase()) ||
            (track.album && track.album.toLowerCase().includes(query.toLowerCase()))
        );
      },
    }),
    {
      name: "search-storage",
    }
  )
);
